<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';

const props = defineProps({
  vipLevel: {
    type: [String, Number],
    default: null,
  },
  size: {
    type: String,
    default: 'md',
    validator: value => ['sm', 'md', 'lg'].includes(value),
  },
  position: {
    type: String,
    default: 'top-right',
    validator: value =>
      ['top-left', 'top-right', 'bottom-left', 'bottom-right'].includes(value),
  },
  // 允许按实例微调位置（单位：px）。不会影响其它使用该组件的地方
  offsetX: {
    type: Number,
    default: 0,
  },
  offsetY: {
    type: Number,
    default: 0,
  },
});

const store = useStore();

// 将VIP级别转换为数字
const vipLevelNumber = computed(() => {
  if (!props.vipLevel) return 0;

  if (typeof props.vipLevel === 'string') {
    const match = props.vipLevel.match(/VIP(\d+)/i);
    if (match) {
      return parseInt(match[1], 10);
    }
  }

  return parseInt(props.vipLevel, 10) || 0;
});

// 获取自定义VIP图标（优先使用数据库中的VIP等级配置）
const customIcon = computed(() => {
  const level = vipLevelNumber.value;
  if (level === 0) return null;

  // 尝试从vipLevels store获取自定义图标
  const vipLevels = store.getters['vipLevels/getActiveVipLevels'] || [];

  // 查找匹配的VIP等级配置
  const matchedLevel = vipLevels.find(vipLevel => {
    // 精确匹配level
    if (vipLevel.level === level) return true;

    // 范围匹配（如果设置了min_value和max_value）
    if (vipLevel.min_value && vipLevel.max_value) {
      return level >= vipLevel.min_value && level <= vipLevel.max_value;
    }

    return false;
  });

  if (matchedLevel && matchedLevel.icon_url) {
    return matchedLevel.icon_url;
  }

  // Fallback: 使用旧的account settings图标
  const accountId = store.getters.getCurrentAccountId;
  const getAccountFn = store.getters['accounts/getAccount'];
  const accountFromStore =
    typeof getAccountFn === 'function' ? getAccountFn(accountId) : {};
  const authAccounts = store.getters.getCurrentUser?.accounts || [];
  const authAccount = authAccounts.find(account => account.id === accountId);
  const effectiveAccount = accountFromStore?.id
    ? accountFromStore
    : authAccount;
  const settings = effectiveAccount?.settings || {};

  // 根据VIP等级获取对应的图标（旧的范围配置）
  if (level >= 1 && level <= 10)
    return settings.vip_1_10 || effectiveAccount?.vip_1_10 || null;
  if (level >= 11 && level <= 20)
    return settings.vip_11_20 || effectiveAccount?.vip_11_20 || null;
  if (level >= 21 && level <= 30)
    return settings.vip_21_30 || effectiveAccount?.vip_21_30 || null;
  if (level >= 31 && level <= 40)
    return settings.vip_31_40 || effectiveAccount?.vip_31_40 || null;
  if (level >= 41 && level <= 50)
    return settings.vip_41_50 || effectiveAccount?.vip_41_50 || null;

  return null;
});

// 获取匹配的VIP等级配置（用于文字样式）
const matchedVipLevel = computed(() => {
  const level = vipLevelNumber.value;
  if (level === 0) return null;

  const vipLevels = store.getters['vipLevels/getActiveVipLevels'] || [];
  return vipLevels.find(vipLevel => {
    if (vipLevel.level === level) return true;
    if (vipLevel.min_value && vipLevel.max_value) {
      return level >= vipLevel.min_value && level <= vipLevel.max_value;
    }
    return false;
  });
});

// 根据VIP等级确定徽章类型
const badgeType = computed(() => {
  const level = vipLevelNumber.value;
  if (level === 0) return null;

  if (level >= 40) return 'diamond';
  if (level >= 30) return 'crown-gold';
  if (level >= 20) return 'crown-silver';
  if (level >= 10) return 'crown-bronze';
  return 'star';
});

// 根据徽章类型返回相应的图标
const badgeIcon = computed(() => {
  const icons = {
    diamond: 'i-lucide-gem',
    'crown-gold': 'i-lucide-crown',
    'crown-silver': 'i-lucide-crown',
    'crown-bronze': 'i-lucide-crown',
    star: 'i-lucide-star',
  };
  return icons[badgeType.value] || 'i-lucide-star';
});

// 根据徽章类型返回颜色类
const badgeColorClasses = computed(() => {
  const colors = {
    diamond: 'bg-purple-600 text-white',
    'crown-gold': 'bg-yellow-500 text-white',
    'crown-silver': 'bg-gray-500 text-white',
    'crown-bronze': 'bg-orange-600 text-white',
    star: 'bg-blue-600 text-white',
  };
  return (
    colors[badgeType.value] || 'bg-indigo-600 text-white ring-2 ring-indigo-400'
  );
});

// 根据size返回尺寸类
const sizeClasses = computed(() => {
  // 以 80x80 的底图为基准缩放渲染
  const sizes = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base',
  };
  return sizes[props.size] || sizes.md;
});

// 根据position返回位置类
const positionClasses = computed(() => {
  const positions = {
    'top-left': 'top-0 left-0',
    'top-right': 'top-0 right-0',
    'bottom-left': 'bottom-0 left-0',
    'bottom-right': 'bottom-0 right-0',
  };
  return positions[props.position] || positions['top-right'];
});

// 根据 position 生成偏移样式，避免覆盖 transform
const offsetStyle = computed(() => {
  const style = {};
  if (props.position.includes('left')) {
    style.marginLeft = `${props.offsetX}px`;
  } else if (props.position.includes('right')) {
    style.marginRight = `${props.offsetX}px`;
  }
  if (props.position.includes('top')) {
    style.marginTop = `${props.offsetY}px`;
  } else if (props.position.includes('bottom')) {
    style.marginBottom = `${props.offsetY}px`;
  }
  return style;
});

// 是否显示VIP等级数字
const showLevel = computed(() => {
  return vipLevelNumber.value >= 10;
});
</script>

<template>
  <div
    v-if="vipLevelNumber > 0"
    class="absolute z-30 flex items-center justify-center rounded-full transform transition-all duration-300 hover:scale-110 font-bold"
    :class="[sizeClasses, positionClasses, customIcon ? '' : badgeColorClasses]"
    :style="offsetStyle"
    :title="`VIP ${vipLevelNumber}`"
  >
    <!-- 使用自定义图标 -->
    <template v-if="customIcon">
      <img
        :src="customIcon"
        :alt="`VIP ${vipLevelNumber}`"
        class="absolute inset-0 w-full h-full rounded-full object-cover"
      />
      <span
        class="relative z-10 font-extrabold"
        :style="{
          color: matchedVipLevel?.text_color || '#FFFFFF',
          textShadow:
            matchedVipLevel?.text_shadow !== false
              ? '0 1px 2px rgba(0,0,0,0.3)'
              : 'none',
        }"
      >
        {{ vipLevelNumber }}
      </span>
    </template>

    <!-- 使用默认徽章样式 -->
    <template v-else>
      <div
        class="w-full h-full rounded-full flex items-center justify-center"
        :class="badgeColorClasses"
      >
        <!-- 高级VIP显示数字 -->
        <span v-if="showLevel" class="font-extrabold text-shadow-sm">
          {{ vipLevelNumber }}
        </span>
        <!-- 低级VIP显示图标 -->
        <span v-else :class="badgeIcon" class="scale-110" />
      </div>
    </template>

    <!-- 钻石级别的额外闪光效果 -->
    <div
      v-if="badgeType === 'diamond' && !customIcon"
      class="absolute inset-0 rounded-full animate-pulse opacity-30 bg-white"
    />
  </div>
</template>

<style scoped>
/* 添加一些动画效果 */
@keyframes sparkle {
  0% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

.animate-sparkle {
  animation: sparkle 2s ease-in-out infinite;
}
</style>
